/*
 * Working in an agency client's marketing account (2026-11-04). The Marketers opens a client's Marketing from the client's
 * page; this bar stays on top so no one posts on the wrong business, and takes them back to that client's page.
 */
import { useNavigate } from "@tanstack/react-router";
import { Banner, Button } from "../components/parts";
import { useSession } from "./session";

const BACK_KEY = "steward.backTo";

interface Back {
  companyId: string;
  path: string;
}

function readBack(): Back | null {
  try {
    const raw = sessionStorage.getItem(BACK_KEY);
    return raw ? (JSON.parse(raw) as Back) : null;
  } catch {
    return null;
  }
}

/** Opens a company's Marketing, remembering the page to come back to. */
export function useOpenMarketing() {
  const { company, switchCompany } = useSession();
  const navigate = useNavigate();
  return (accountId: string, backPath: string) => {
    try {
      sessionStorage.setItem(BACK_KEY, JSON.stringify({ companyId: company.id, path: backPath } satisfies Back));
    } catch {
      // Not saved; Back then goes to the agency's Clients.
    }
    switchCompany(accountId);
    void navigate({ to: "/marketing" });
  };
}

export function WorkingFor() {
  const { company, companies, switchCompany } = useSession();
  const navigate = useNavigate();
  const agency = companies.find((c) => c.id === company.parentId);
  const saved = readBack();
  const back = saved && saved.companyId === agency?.id ? saved : null;
  const goBack = () => {
    if (!agency) return;
    switchCompany(agency.id);
    void navigate({ to: back?.path ?? "/clients" });
  };
  return (
    <Banner
      action={
        agency ? (
          <Button type="secondary" size="small" icon="arrowLeft" onClick={goBack}>
            Back to the client
          </Button>
        ) : null
      }
    >
      Working on <b>{company.name}</b>
      {agency ? ` for ${agency.name}` : ""}. Everything here posts and runs for this business.
    </Banner>
  );
}
